import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { VehicleMake, VehicleModel } from '../../services/vehicleApi';

interface VehicleResultCardProps {
  make: VehicleMake;
  model: VehicleModel;
  availableShops?: number;
  nearestShopDistance?: number;
  compatibleTyres?: number;
  onViewDetails?: () => void;
  onRequestQuote?: () => void;
  onBookService?: () => void;
  className?: string;
}

const VehicleResultCard: React.FC<VehicleResultCardProps> = ({
  make,
  model,
  availableShops = 0,
  nearestShopDistance,
  compatibleTyres = 0,
  onViewDetails,
  onRequestQuote,
  onBookService,
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Generate vehicle image URL (placeholder for now, could be real images)
  const getVehicleImageUrl = (makeName: string, modelName: string) => {
    // This could be replaced with actual vehicle image service
    const fallbackImages = {
      'Toyota': '/images/vehicles/toyota-default.jpg',
      'BMW': '/images/vehicles/bmw-default.jpg',
      'Mercedes-Benz': '/images/vehicles/mercedes-default.jpg',
      'Audi': '/images/vehicles/audi-default.jpg',
      'Honda': '/images/vehicles/honda-default.jpg',
      'Ford': '/images/vehicles/ford-default.jpg'
    };
    return fallbackImages[makeName as keyof typeof fallbackImages] || `/images/vehicles/default-car.jpg`;
  };

  // Get brand color scheme
  const getBrandColors = (makeName: string) => {
    const brandColors = {
      'Toyota': { primary: 'from-red-500 to-red-600', accent: 'text-red-600', bg: 'bg-red-50' },
      'BMW': { primary: 'from-blue-600 to-blue-700', accent: 'text-blue-600', bg: 'bg-blue-50' },
      'Mercedes-Benz': { primary: 'from-gray-700 to-gray-800', accent: 'text-gray-700', bg: 'bg-gray-50' },
      'Audi': { primary: 'from-red-600 to-red-700', accent: 'text-red-600', bg: 'bg-red-50' },
      'Honda': { primary: 'from-blue-500 to-blue-600', accent: 'text-blue-600', bg: 'bg-blue-50' },
      'Ford': { primary: 'from-blue-700 to-blue-800', accent: 'text-blue-700', bg: 'bg-blue-50' }
    };
    return brandColors[makeName as keyof typeof brandColors] || {
      primary: 'from-gray-500 to-gray-600',
      accent: 'text-gray-600',
      bg: 'bg-gray-50'
    };
  };

  const brandColors = getBrandColors(make.name);
  const yearRange = model.year_from && model.year_to 
    ? `${model.year_from} - ${model.year_to}`
    : model.year_from 
    ? `${model.year_from} - Present`
    : 'Year Range Not Specified';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${
        isHovered ? 'shadow-2xl ring-2 ring-blue-500/20' : ''
      } ${className}`}
    >
      {/* Vehicle Image Header */}
      <div className="relative h-48 overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${brandColors.primary} opacity-10`}></div>
        
        {!imageError ? (
          <img
            src={getVehicleImageUrl(make.name, model.name)}
            alt={`${make.name} ${model.name}`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${brandColors.primary}`}>
            <div className="text-center text-white">
              <svg className="w-16 h-16 mx-auto mb-2 opacity-80" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              <p className="font-medium">{make.name}</p>
              <p className="text-sm opacity-90">{model.name}</p>
            </div>
          </div>
        )}

        {/* Overlay Badges */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {make.popular && (
            <span className="px-2 py-1 bg-orange-500 text-white text-xs font-medium rounded-full flex items-center">
              ⭐ Popular
            </span>
          )}
          {compatibleTyres > 0 && (
            <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
              {compatibleTyres} Tyres Available
            </span>
          )}
        </div>

        {/* Availability Indicator */}
        <div className="absolute top-4 right-4">
          <div className={`w-3 h-3 rounded-full ${availableShops > 0 ? 'bg-green-400' : 'bg-gray-400'}`}></div>
        </div>
      </div>

      {/* Vehicle Information */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold text-gray-900 truncate">
              {make.name} {model.name}
            </h3>
            {model.default_tyre_size && (
              <span className={`px-3 py-1 ${brandColors.bg} ${brandColors.accent} text-sm font-medium rounded-full`}>
                {model.default_tyre_size}
              </span>
            )}
          </div>
          <p className="text-gray-600 text-sm">{yearRange}</p>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <svg className="w-4 h-4 text-blue-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-900">{availableShops}</p>
            <p className="text-xs text-gray-500">Shops</p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <svg className="w-4 h-4 text-green-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-900">{compatibleTyres}</p>
            <p className="text-xs text-gray-500">Compatible</p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <svg className="w-4 h-4 text-purple-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {nearestShopDistance ? `${nearestShopDistance}km` : '-'}
            </p>
            <p className="text-xs text-gray-500">Nearest</p>
          </div>
        </div>

        {/* Action Buttons — quote/booking flows removed; contact the shop directly */}
        <div className="flex space-x-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onViewDetails}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-xl transition-colors duration-200 text-sm"
          >
            View Details
          </motion.button>
        </div>

      </div>

      {/* Hover Overlay Effect */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none"
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default VehicleResultCard;